import type { PlaylistItem } from '../../types/playlist';
import type { EpisodeMetadata, Series } from '../../types/series';

import { mediaURL } from './urlFormatting';
import { secondsToISO8601 } from './datetime';

const generatePersons = (value: unknown) => {
  if (typeof value !== 'string') return undefined;
  return value.split(',').map((name) => ({ '@type': 'Person', name }));
};

export const generateMovieJSONLD = (item: PlaylistItem, origin: string) => {
  const { mediaid: id, title } = item;
  const movieCanonical = `${origin}${mediaURL({ id, title })}`;

  return JSON.stringify({
    '@context': 'http://schema.org/',
    '@type': 'VideoObject',
    '@id': movieCanonical,
    name: item.title,
    description: item.description,
    duration: secondsToISO8601(item.duration, true),
    thumbnailUrl: item.image,
    uploadDate: secondsToISO8601(item.pubdate),
  });
};

export const generateSeriesMetadata = (series: Series, media: PlaylistItem, seriesId: string, origin: string) => {
  // Use playlist for old flow and media id for a new flow
  const seriesCanonical = `${origin}/m/${seriesId}`;

  return {
    '@type': 'TVSeries',
    '@id': seriesCanonical,
    name: media.title,
    numberOfEpisodes: String(series?.episode_count || 0),
    numberOfSeasons: String(series?.season_count || 0),
    genres: typeof media.genre === 'string' ? media.genre.split(',') : undefined,
    actor: generatePersons(media?.actors),
    director: generatePersons(media?.directors),
    creator: generatePersons(media?.creators),
  };
};

export const generateEpisodeJSONLD = (
  series: Series,
  media: PlaylistItem,
  origin: string,
  episode: PlaylistItem | undefined,
  episodeMetadata: EpisodeMetadata | undefined,
) => {
  const episodeCanonical = `${origin}/m/${series.series_id}?e=${episode?.mediaid}`;
  const seriesMetadata = generateSeriesMetadata(series, media, series.series_id, origin);

  if (!episode) {
    return JSON.stringify(seriesMetadata);
  }

  return JSON.stringify({
    '@context': 'http://schema.org/',
    '@type': 'TVEpisode',
    '@id': episodeCanonical,
    episodeNumber: episodeMetadata?.episodeNumber || '0',
    name: episode.title,
    datePublished: secondsToISO8601(episode.pubdate),
    partOfSeries: seriesMetadata,
    genre: typeof episode.genre === 'string' ? episode.genre.split(',') : undefined,
    actor: generatePersons(media?.actors ?? episode?.actors),
    director: generatePersons(media?.directors ?? episode?.directors),
    creator: generatePersons(media?.creators ?? episode?.creators),
  });
};
